import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CheckCircle2, AlertTriangle, XCircle, Copy, 
  User, Users, Calendar, DollarSign, Loader2,
  ArrowLeft, Check, FileSpreadsheet
} from 'lucide-react';

export function ImportPreview({ batchId, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [titles, setTitles] = useState([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadBatch();
  }, [batchId]);

  const loadBatch = async () => {
    try {
      const res = await api.get(`/finance/import-titles/batches/${batchId}`);
      setBatch(res.data.batch);
      setTitles(res.data.titles || []);
    } catch (error) {
      console.error('Erro ao carregar lote:', error);
      toast.error('Erro ao carregar dados do lote');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (force = false) => {
    setConfirming(true);
    try {
      await onConfirm(batchId, force);
    } finally {
      setConfirming(false);
    }
  };

  const getMatchingBadge = (status) => {
    const config = {
      matched: { label: 'Vinculado', variant: 'default', icon: CheckCircle2, color: 'text-green-500' },
      guardian_not_found: { label: 'Responsável não encontrado', variant: 'destructive', icon: XCircle, color: 'text-red-500' },
      student_not_found: { label: 'Aluno não encontrado', variant: 'destructive', icon: XCircle, color: 'text-red-500' },
      multiple_matches: { label: 'Múltiplos resultados', variant: 'warning', icon: AlertTriangle, color: 'text-yellow-500' },
      conflict: { label: 'Conflito de vínculo', variant: 'warning', icon: AlertTriangle, color: 'text-orange-500' },
      pending_review: { label: 'Revisão necessária', variant: 'secondary', icon: AlertTriangle, color: 'text-slate-500' }
    };
    const c = config[status] || config.pending_review;
    const Icon = c.icon;
    return (
      <Badge variant={c.variant} className="gap-1 text-xs">
        <Icon className={`w-3 h-3 ${c.color}`} />
        {c.label}
      </Badge>
    );
  };

  const getDuplicateBadge = (status) => {
    if (status === 'none') return null;
    const config = {
      exact: { label: 'Duplicado exato', variant: 'destructive' },
      possible: { label: 'Possível duplicado', variant: 'warning' }
    };
    const c = config[status];
    return c ? <Badge variant={c.variant} className="text-xs">{c.label}</Badge> : null;
  };

  const getTitleStatusBadge = (status) => {
    const config = {
      a_vencer: { label: 'A vencer', className: 'bg-blue-100 text-blue-700' },
      em_aberto: { label: 'Em aberto', className: 'bg-red-100 text-red-700' },
      parcial: { label: 'Parcial', className: 'bg-yellow-100 text-yellow-700' },
      pago: { label: 'Pago', className: 'bg-green-100 text-green-700' }
    };
    const c = config[status] || config.a_vencer;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!batch) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Lote não encontrado</p>
          <Button className="mt-4" onClick={onCancel}>Voltar</Button>
        </CardContent>
      </Card>
    );
  }

  const validTitles = titles.filter(t => t.is_valid && !t.is_duplicate);
  const warningTitles = titles.filter(t => t.has_warning && !t.has_error);
  const errorTitles = titles.filter(t => t.has_error);
  const duplicateTitles = titles.filter(t => t.is_duplicate);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6 text-purple-500" />
              <div>
                <CardTitle>{batch.filename}</CardTitle>
                <CardDescription>
                  Prévia da importação - {new Date(batch.created_at).toLocaleString('pt-BR')}
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" onClick={onCancel}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-3 rounded-lg bg-slate-100">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{batch.total_rows}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <p className="text-sm text-green-600">Válidos</p>
              <p className="text-2xl font-bold text-green-700">{validTitles.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-100">
              <p className="text-sm text-yellow-600">Alertas</p>
              <p className="text-2xl font-bold text-yellow-700">{warningTitles.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-100">
              <p className="text-sm text-red-600">Erros</p>
              <p className="text-2xl font-bold text-red-700">{errorTitles.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-orange-100">
              <p className="text-sm text-orange-600">Duplicados</p>
              <p className="text-2xl font-bold text-orange-700">{duplicateTitles.length}</p>
            </div>
          </div>

          <div className="mt-4 p-4 rounded-lg bg-purple-50 border border-purple-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-purple-600">Valor Total</p>
                <p className="text-xl font-bold text-purple-700">
                  R$ {batch.total_amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-600">Valor Importável</p>
                <p className="text-xl font-bold text-green-700">
                  R$ {batch.importable_amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Titles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento dos Títulos</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {titles.map((title, idx) => (
                <div 
                  key={title.id || idx}
                  className={`p-4 rounded-lg border transition-all ${
                    title.has_error ? 'bg-red-50 border-red-200' :
                    title.is_duplicate ? 'bg-orange-50 border-orange-200' :
                    title.has_warning ? 'bg-yellow-50 border-yellow-200' :
                    'bg-green-50 border-green-200'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                    <div className="space-y-2 flex-1">
                      {/* Row number and badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Linha {title.row_number}</span>
                        {getMatchingBadge(title.matching_status)}
                        {getDuplicateBadge(title.duplicate_status)}
                        {getTitleStatusBadge(title.title_status)}
                      </div>

                      {/* Main info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{title.student_name}</span>
                          {title.student_id && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span>{title.guardian_name || 'Não informado'}</span>
                          {title.guardian_id && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>
                            {title.due_date 
                              ? (() => { const m = String(title.due_date).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : title.due_date; })()
                              : 'Sem vencimento'
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">
                            R$ {title.amount_original?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                          {title.amount_paid > 0 && (
                            <span className="text-green-600">
                              (Pago: R$ {title.amount_paid?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Category and Class */}
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          {title.category_standard || title.category_external}
                        </span>
                        {title.class_name && (
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {title.class_name}
                          </span>
                        )}
                        {title.external_bill_number && (
                          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                            Boleto #{title.external_bill_number}
                          </span>
                        )}
                      </div>

                      {/* Error/Warning message */}
                      {(title.error_message || title.warning_message) && (
                        <div className={`text-sm ${title.has_error ? 'text-red-600' : 'text-yellow-600'}`}>
                          {title.error_message || title.warning_message}
                        </div>
                      )}

                      {/* Duplicate reason */}
                      {title.duplicate_reason && (
                        <div className="text-sm text-orange-600">
                          {title.duplicate_reason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {validTitles.length > 0 ? (
                <span className="text-green-600">
                  <Check className="w-4 h-4 inline mr-1" />
                  {validTitles.length} título(s) pronto(s) para importar
                </span>
              ) : (
                <span className="text-red-600">
                  <XCircle className="w-4 h-4 inline mr-1" />
                  Nenhum título válido para importar
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onCancel}>
                Cancelar
              </Button>
              {warningTitles.length > 0 && validTitles.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={() => handleConfirm(true)}
                  disabled={confirming}
                >
                  {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Importar com Alertas
                </Button>
              )}
              <Button 
                onClick={() => handleConfirm(false)}
                disabled={validTitles.length === 0 || confirming}
                className="bg-gradient-to-r from-purple-600 to-green-500 hover:from-purple-700 hover:to-green-600"
              >
                {confirming ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirmar Importação ({validTitles.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
